import { useQuery } from '@tanstack/react-query'
import {
  createBatchFetcherByKey,
  extractIdFromIri,
  fetchApiWithToken,
  type WithHydraPagination
} from '~/api/utils'
import type { ContractHead } from '../types'

type ContractBatchResult = {
  orderId: number
  contracts: ContractHead[]
}

const contractsBatcher = createBatchFetcherByKey<ContractBatchResult, number>(
  (item) => item.orderId
)({
  fetcher: async (orderIds: number[]) => {
    const results: ContractBatchResult[] = []
    const uniqueOrderIds = [...new Set(orderIds)]
    for (const orderId of uniqueOrderIds) {
      const params = new URLSearchParams()
      params.append('order', String(orderId))
      const response = await fetchApiWithToken<
        WithHydraPagination<ContractHead>
      >({
        method: 'get',
        url: `/v1/contracts?${params.toString()}`,
        useLDJson: true
      })
      const contracts = response['hydra:member'] ?? []
      results.push({ orderId, contracts })
    }
    return results
  },
  delay: 10
})

export const useContracts = ({
  applicantId,
  orderId,
  enabled = true
}: {
  applicantId?: number
  orderId?: number
  enabled?: boolean
}) => {
  return useQuery<ContractHead[]>({
    queryKey: ['contracts', orderId],
    queryFn: async () => {
      if (!orderId) {
        return []
      }
      const result = await contractsBatcher.fetch(orderId)
      return result.contracts
    },
    enabled: enabled && !!orderId,
    staleTime: 60_000,
    select: (data) => {
      if (!applicantId) {
        return data
      }
      return data.filter((contract) => {
        const contractApplicantId = extractIdFromIri(contract.applicant)
        return contractApplicantId === applicantId
      })
    }
  })
}
